import * as signalR from '@microsoft/signalr';
import { API_URL } from '@/http';
import { auth } from '@/auth';
import { connectWithBackoff } from './connectWithBackoff';

/**
 * Режимы push-хаба. `disabled` — стабильное состояние до перезагрузки страницы:
 * хаб отключён на бэке (404) либо ретраи исчерпаны; поллинга у таких хабов нет.
 */
export type PushHubMode = 'connecting' | 'push' | 'disabled';

export interface IPushHubOptions<TPayload> {
    /** Имя хаба в логах retry-цикла */
    name: string;
    /** Путь хаба относительно `API_URL` */
    path: string;
    /** Имя серверного события */
    event: string;
    /** Проверка полезной нагрузки; `null`/`undefined` отсекается до вызова */
    isValidPayload: (payload: TPayload) => boolean;
}

export type PushHubCallback<TPayload> = (payload: TPayload) => void;

/**
 * Базовый singleton-клиент SignalR-хаба «сервер толкнул событие, клиент решает, что делать»:
 * одно соединение, одно событие, список подписчиков. Наследник даёт конструктору свои
 * параметры и заводит доменное имя подписки поверх {@link subscribe}.
 */
export class PushHubClient<TPayload> {
    private connection: signalR.HubConnection;
    private callbacks: PushHubCallback<TPayload>[] = [];
    private reconnectedCallbacks: Array<() => void> = [];
    private stopPromise: Promise<void> | null = null;
    private ensurePromise: Promise<void> | null = null;
    private stopped = false;
    private wasConnected = false;
    private backendDisabled = false;
    private _mode: PushHubMode = 'connecting';
    private readonly name: string;

    constructor({ name, path, event, isValidPayload }: IPushHubOptions<TPayload>) {
        this.name = name;

        const logLevel = import.meta.env.PROD ? signalR.LogLevel.Warning : signalR.LogLevel.Trace;

        this.connection = new signalR.HubConnectionBuilder()
            .configureLogging(logLevel)
            .withUrl(`${API_URL}${path}`, {
                accessTokenFactory: () => auth.pageAccessToken() ?? '',
            })
            .withAutomaticReconnect()
            .build();

        this.connection.on(event, (payload: TPayload) => {
            if (!payload || !isValidPayload(payload)) return;
            this.callbacks.forEach((cb) => cb(payload));
        });

        this.connection.onreconnected(() => this.emitReconnected());
    }

    private emitReconnected(): void {
        this.reconnectedCallbacks.forEach((cb) => cb());
    }

    public get mode(): PushHubMode {
        return this._mode;
    }

    public get isConnected(): boolean {
        return this.connection.state === signalR.HubConnectionState.Connected;
    }

    public async start(): Promise<void> {
        this.stopped = false;
        this._mode = 'connecting';
        // Соединение было и оборвалось (автопереподключение исчерпано) — успешный старт его восстанавливает.
        const restoring = this.wasConnected && this.connection.state === signalR.HubConnectionState.Disconnected;
        const result = await connectWithBackoff(this.connection, this.name, {
            isStopped: () => this.stopped,
            consumeStopPromise: () => {
                const p = this.stopPromise;
                this.stopPromise = null;
                return p;
            },
            on404: () => {
                this._mode = 'disabled';
                this.backendDisabled = true;
            },
        });
        if (result === 'connected') {
            this._mode = 'push';
            this.wasConnected = true;
            if (restoring && !this.stopped) this.emitReconnected();
        } else if (result === 'failed') {
            this._mode = 'disabled';
        }
    }

    /**
     * Мягкий повторный старт для долгоживущего потребителя: no-op, пока соединение живо, старт уже идёт или хаб
     * выключен на сервере (404). После окончательного отказа — исчерпаны попытки старта или автопереподключения —
     * пробует подключиться заново; ошибки не пробрасывает.
     */
    public ensureStarted(): void {
        if (this.backendDisabled || this.ensurePromise) return;
        if (this.connection.state !== signalR.HubConnectionState.Disconnected) return;
        const start = this.start()
            .catch((err) => {
                console.warn(`${this.name}: start failed, push disabled`, err);
            })
            .finally(() => {
                if (this.ensurePromise === start) this.ensurePromise = null;
            });
        this.ensurePromise = start;
    }

    public stop(): void {
        this.stopped = true;
        // Следующий старт — новое подключение (например, после повторного входа), а не восстановление.
        this.wasConnected = false;
        this.callbacks = [];
        this.reconnectedCallbacks = [];

        if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
            this.stopPromise = this.connection.stop().catch(() => {});
        }
    }

    public subscribe(callback: PushHubCallback<TPayload>): () => void {
        this.callbacks.push(callback);
        return () => {
            this.callbacks = this.callbacks.filter((cb) => cb !== callback);
        };
    }

    /**
     * Соединение восстановлено после обрыва: автопереподключение SignalR или повторный старт после отказа. События,
     * разосланные без соединения, потеряны — подписчик перечитывает своё состояние. Первое подключение сюда не
     * попадает.
     */
    public onReconnected(callback: () => void): () => void {
        this.reconnectedCallbacks.push(callback);
        return () => {
            this.reconnectedCallbacks = this.reconnectedCallbacks.filter((cb) => cb !== callback);
        };
    }
}
